import React, { useState, useMemo, useCallback } from 'react';
import { Calendar, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { useListAdminSitesQuery, useListPortfolioSitesQuery } from '../../redux/api/portfolioApi';
import { useAppSelector } from '../../redux/hooks';
import PortfolioEventsCalendar from '../dashboard/modules/PortfolioEventsCalendar';
import UpcomingCalendarModule from '../dashboard/modules/UpcomingCalendarModule';
import {
  createMockUpcomingEvents,
  type UpcomingPortfolioEvent,
  UPCOMING_EVENT_TYPE_CONFIG,
  CATEGORY_COLORS,
} from '../../mocks/upcomingEventsMock';

type ViewMode = 'list' | 'calendar';

function getMonthDays(year: number, month: number) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay(); // 0=Sun
  const totalDays = last.getDate();
  return { startDay, totalDays };
}

function getDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysUntil(dateString: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateString);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function urgencyColor(days: number): string {
  if (days < 0) return '#EF4444';
  if (days <= 7) return '#EF4444';
  if (days <= 30) return '#F97316';
  if (days <= 90) return '#F59E0B';
  if (days <= 365) return '#3B82F6';
  return '#6B7280';
}

/** Month-grid calendar view */
const MonthCalendarView: React.FC<{
  events: UpcomingPortfolioEvent[];
  onEventClick?: (event: UpcomingPortfolioEvent) => void;
}> = ({ events, onEventClick }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, UpcomingPortfolioEvent[]>();
    events.forEach((e) => {
      const d = new Date(e.due_date);
      const key = getDateKey(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return map;
  }, [events]);

  const { startDay, totalDays } = useMemo(() => getMonthDays(year, month), [year, month]);
  const monthLabel = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const prevMonth = useCallback(() => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDate(null);
  }, [month]);

  const nextMonth = useCallback(() => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDate(null);
  }, [month]);

  const goToday = useCallback(() => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setSelectedDate(null);
  }, []);

  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    return eventsByDate.get(selectedDate) || [];
  }, [selectedDate, eventsByDate]);

  // Build grid cells
  const cells: Array<{ day: number | null; key: string; dateKey: string; isToday: boolean }> = [];
  const blanks = startDay === 0 ? 6 : startDay - 1; // shift to Monday start
  for (let i = 0; i < blanks; i++) {
    cells.push({ day: null, key: `blank-${i}`, dateKey: '', isToday: false });
  }
  for (let d = 1; d <= totalDays; d++) {
    const date = new Date(year, month, d);
    const dateKey = getDateKey(date);
    const isToday = dateKey === getDateKey(today);
    cells.push({ day: d, key: dateKey, dateKey, isToday });
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Calendar Grid */}
      <div className="flex-1 min-w-0">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors">
              <ChevronLeft className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
            </button>
            <h2 className="text-lg font-semibold min-w-[200px] text-center" style={{ color: 'var(--color-text-primary)' }}>
              {monthLabel}
            </h2>
            <button onClick={nextMonth} className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors">
              <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
            </button>
          </div>
          <button
            onClick={goToday}
            className="px-3 py-1 rounded text-xs font-medium transition-colors"
            style={{
              backgroundColor: 'rgba(23, 196, 128, 0.15)',
              color: '#17c480',
            }}
          >
            Today
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px mb-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div
              key={d}
              className="text-center text-xs font-medium py-1.5"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-px">
          {cells.map((cell) => {
            if (cell.day === null) {
              return <div key={cell.key} className="aspect-square" />;
            }

            const dayEvents = eventsByDate.get(cell.dateKey) || [];
            const isSelected = selectedDate === cell.dateKey;
            const maxSev = dayEvents.reduce((acc, e) => {
              const order = { critical: 0, high: 1, medium: 2, low: 3 };
              return (order[e.severity] ?? 4) < (order[acc as keyof typeof order] ?? 4) ? e.severity : acc;
            }, 'low');

            return (
              <button
                key={cell.key}
                onClick={() => setSelectedDate(isSelected ? null : cell.dateKey)}
                className="min-h-[80px] rounded-lg p-1 text-left transition-all relative flex flex-col"
                style={{
                  backgroundColor: isSelected
                    ? 'rgba(23, 196, 128, 0.15)'
                    : 'transparent',
                  border: cell.isToday ? '2px solid #17c480' : '1px solid var(--color-border)',
                }}
              >
                <span
                  className="text-xs font-medium mb-0.5"
                  style={{
                    color: cell.isToday
                      ? '#17c480'
                      : dayEvents.length > 0
                        ? 'var(--color-text-primary)'
                        : 'var(--color-text-muted)',
                  }}
                >
                  {cell.day}
                </span>
                {dayEvents.length > 0 && (
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0 overflow-hidden">
                    {dayEvents.slice(0, 3).map((e) => {
                      const config = UPCOMING_EVENT_TYPE_CONFIG[e.event_type];
                      return (
                        <div
                          key={e.id}
                          className="rounded px-1 py-px truncate text-[9px] font-medium leading-tight"
                          style={{
                            backgroundColor: `${config.color}20`,
                            color: config.color,
                            borderLeft: `2px solid ${config.color}`,
                          }}
                          title={e.title}
                        >
                          {config.label}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <span className="text-[9px] px-1" style={{ color: 'var(--color-text-muted)' }}>
                        +{dayEvents.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected date detail panel */}
      <div
        className="w-80 shrink-0 rounded-lg p-4 overflow-y-auto"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
        }}
      >
        {selectedDate ? (
          <>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
              {new Date(selectedDate).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </h3>
            {selectedEvents.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No events on this date</p>
            ) : (
              <div className="space-y-2">
                {selectedEvents.map((event) => {
                  const config = UPCOMING_EVENT_TYPE_CONFIG[event.event_type];
                  const days = daysUntil(event.due_date);
                  return (
                    <button
                      key={event.id}
                      onClick={() => onEventClick?.(event)}
                      className="w-full text-left p-3 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)]"
                      style={{ border: '1px solid var(--color-border)' }}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0"
                          style={{ backgroundColor: config.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {event.title}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                            {event.site_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: `${config.color}15`,
                                color: config.color,
                              }}
                            >
                              {config.label}
                            </span>
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                              style={{
                                backgroundColor: `${urgencyColor(days)}15`,
                                color: urgencyColor(days),
                              }}
                            >
                              {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}
                            </span>
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: `${CATEGORY_COLORS[event.category]}15`,
                                color: CATEGORY_COLORS[event.category],
                              }}
                            >
                              {event.category}
                            </span>
                          </div>
                          <p className="text-xs mt-1.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                            {event.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Calendar className="w-8 h-8 mb-2" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              Select a date to view events
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const PortfolioEventsCalendarTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Fetch sites — respect portfolio selection
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const profile = useAppSelector((state) => state.auth.profile);
  const isSuperAdmin = ((profile.email || '') as string).toLowerCase().endsWith('@engramiq.com');
  const usePortfolioScoping = !!selectedPortfolioId;

  const { data: adminSitesData, isLoading: adminLoading } = useListAdminSitesQuery({ limit: 100 }, {
    skip: !isSuperAdmin || usePortfolioScoping,
  });
  const { data: portfolioSitesData, isLoading: portfolioLoading } = useListPortfolioSitesQuery(
    { portfolio_id: selectedPortfolioId || '' },
    { skip: !selectedPortfolioId }
  );
  const isLoading = usePortfolioScoping ? portfolioLoading : adminLoading;
  const allSites = useMemo(() => {
    const raw = usePortfolioScoping ? portfolioSitesData : adminSitesData;
    return (raw as any)?.items || [];
  }, [usePortfolioScoping, portfolioSitesData, adminSitesData]);

  // Custom portfolio filter
  const [customSiteIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('eg-custom-portfolio');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const displaySites = useMemo(() => {
    if (customSiteIds.length === 0) return allSites;
    const idSet = new Set(customSiteIds);
    return allSites.filter(s => idSet.has(s.site_id || s.id || ''));
  }, [allSites, customSiteIds]);

  // Generate mock events
  const upcomingEvents: UpcomingPortfolioEvent[] = useMemo(() => {
    if (displaySites.length === 0) return [];
    return createMockUpcomingEvents(displaySites, 5);
  }, [displaySites]);

  if (isLoading) {
    return <div className="eg-loading p-8">Loading portfolio events...</div>;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div
        className="shrink-0 relative overflow-hidden"
        style={{ background: `linear-gradient(160deg, var(--color-dash-header-bg) 0%, var(--color-dash-header-bg-subtle) 100%)` }}
      >
        <div className="relative px-6 pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5">
                <Calendar className="w-5 h-5 text-[#17c480]" />
                <h1
                  className="text-[22px] font-bold tracking-tight"
                  style={{ color: 'var(--color-dash-header-text)' }}
                >
                  Portfolio Events Calendar
                </h1>
              </div>
              <p className="mt-1.5 text-sm" style={{ color: 'var(--color-dash-header-secondary)' }}>
                {upcomingEvents.length} upcoming events across {displaySites.length} sites
              </p>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-0.5 rounded-md p-0.5" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[#17c480] text-white'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-[#17c480] text-white'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Calendar
              </button>
            </div>
          </div>
        </div>

        <div
          className="h-[2px]"
          style={{
            background: 'linear-gradient(90deg, #17c480 0%, rgba(23, 196, 128, 0.3) 40%, transparent 80%)',
          }}
        />
      </div>

      {/* Heatmap summary */}
      <div className="shrink-0 px-6 pt-4">
        <UpcomingCalendarModule events={upcomingEvents} />
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto px-6 py-4"
        style={{ background: 'var(--color-zone-recessed)' }}
      >
        {viewMode === 'list' ? (
          <PortfolioEventsCalendar events={upcomingEvents} />
        ) : (
          <MonthCalendarView events={upcomingEvents} />
        )}
      </div>
    </div>
  );
};

export default PortfolioEventsCalendarTab;
